// ExpandedForm.jsx
// Import Libraries
import React, { useState, useContext, useMemo } from 'react';

// MUI
import {
  Box,
  Stack,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
} from '@mui/material';

// Import Components
import ProFlag from '../../Elements/ProFlag.js';
import { MuiCreatableSelect } from './MuiCreatableSelect.js';

// Context
import ScriptContext from '../../../context/Script';
import ProjectViewContext from '../../../context/ProjectView';

// Hooks
import useScenePermissions from '../../../hooks/useScenePermissions.js';

function MuiCreatableMultiSelect(props) {
  return <MuiCreatableSelect {...props} />;
}

function ExpandedForm({
  scene,
  shot,
  dictionaries,
  handleShotFieldChange,
  addNewUserDictionary,
  specialEquipmentOptions,
  addNewSpecialEquipment,
}) {
  const { script, setScript } = useContext(ScriptContext);
  const [tempNotes, setTempNotes] = useState({});
  const { view, lockedFields, lockField, unlockField } = useContext(ProjectViewContext);

  const { isReadOnly: isViewerReadOnly, isSceneLocked, isGenerationLocked } = useScenePermissions(
    scene,
    script,
  );
  const isReadOnly = isViewerReadOnly || isSceneLocked || isGenerationLocked;

  const handleUpdateNotes = async (_sceneNumber, shotId) => {
    if (tempNotes[shotId] === undefined) return;
    handleShotFieldChange(shotId, 'notes', tempNotes[shotId]);
    setTempNotes((prev) => ({ ...prev, [shotId]: undefined }));
  };

  const proDisabled =
    !isReadOnly && script.features && script.features.toLowerCase() !== 'basic' ? false : true;

  const groupLabelSx = {
    fontSize: 12,
    letterSpacing: '0.08em',
    fontWeight: 700,
    color: 'text.secondary',
    textTransform: 'uppercase',
  };

  const pillGroupSx = {
    '& .MuiToggleButtonGroup-grouped': {
      borderRadius: '12px !important',
      borderColor: 'divider',
      px: 1.5,
      py: 1,
      minWidth: 44,
      fontWeight: 700,
      textTransform: 'uppercase',
      lineHeight: 1,
      marginRight: '5px !important',
      bgcolor: '#fff',
    },
    '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
      marginLeft: 0,
    },
  };

  const cameraBtnSx = {
    bgcolor: '#fff',
    '&.Mui-selected': {
      bgcolor: '#7A64EB !important',
      color: '#fff',
      borderColor: '#7A64EB',
      '&:hover': { bgcolor: '#6a56e0 !important' },
    },
  };

  const flagBtnSx = {
    bgcolor: '#fff',
    '&.Mui-selected': {
      bgcolor: '#7A64EB !important',
      color: '#fff',
      borderColor: '#7A64EB',
      '&:hover': { bgcolor: '#6a56e0 !important' },
    },
  };

  const isSingleColumn =
    view?.viewColumns === 1 || view?.view?.columns === 1 || view?.viewColumns === '1';

  const singleRow6ColsSx = {
    display: 'grid',
    gap: 2,
    mt: 0.5,
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(6, minmax(0, 1fr))',
    },
    alignItems: 'start',
  };

  const specialEquipmentField = (
    <MuiCreatableSelect
      optionList={specialEquipmentOptions}
      selected={shot.special_equipment || ''}
      baseItem={shot}
      selectedFieldName="special_equipment"
      changeHandler={handleShotFieldChange}
      addOption={script.level === 'owner' ? addNewSpecialEquipment : null}
      label="Special Equipment"
      id={`shot-${shot.id}-special_equipment`}
      readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-special_equipment`)}
      lockField={lockField}
      unlockField={unlockField}
    />
  );

  const singleRowNotesSx = {
    display: 'grid',
    gap: 2,
    mt: 2,
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(4, minmax(0, 1fr))',
    },
    alignItems: 'start',
  };

  return (
    <Box
      className={`${shot.expanded ? '' : 'hidden'}`}
      sx={{ borderTop: '1px dashed #7A64EB', paddingTop: '10px' }}
    >
      {isSingleColumn ? (
        <>
          <Box sx={singleRow6ColsSx}>
            <MuiCreatableMultiSelect
              optionList={dictionaries['movement']}
              selected={shot.movement}
              baseItem={shot}
              selectedFieldName="movement"
              changeHandler={handleShotFieldChange}
              addOption={script.level === 'owner' ? addNewUserDictionary : null}
              label="Movement"
              id={`shot-${shot.id}-movement`}
              readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-movement`)}
              lockField={lockField}
              unlockField={unlockField}
            />

            <MuiCreatableMultiSelect
              optionList={dictionaries['camera']}
              selected={shot.camera}
              baseItem={shot}
              selectedFieldName="camera"
              changeHandler={handleShotFieldChange}
              addOption={script.level === 'owner' ? addNewUserDictionary : null}
              label="Camera"
              id={`shot-${shot.id}-camera`}
              readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-camera`)}
              lockField={lockField}
              unlockField={unlockField}
            />

            <MuiCreatableMultiSelect
              optionList={dictionaries['focus']}
              selected={shot.focus}
              baseItem={shot}
              selectedFieldName="focus"
              changeHandler={handleShotFieldChange}
              addOption={script.level === 'owner' ? addNewUserDictionary : null}
              label="Focus"
              id={`shot-${shot.id}-focus`}
              readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-focus`)}
              lockField={lockField}
              unlockField={unlockField}
            />

            <MuiCreatableMultiSelect
              optionList={dictionaries['focal']}
              selected={shot.focal}
              baseItem={shot}
              selectedFieldName="focal"
              changeHandler={handleShotFieldChange}
              addOption={script.level === 'owner' ? addNewUserDictionary : null}
              label="Focal"
              id={`shot-${shot.id}-focal`}
              readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-focal`)}
              lockField={lockField}
              unlockField={unlockField}
            />

            <MuiCreatableMultiSelect
              optionList={dictionaries['frame']}
              selected={shot.frame}
              baseItem={shot}
              selectedFieldName="frame"
              changeHandler={handleShotFieldChange}
              addOption={script.level === 'owner' ? addNewUserDictionary : null}
              label="Frame"
              id={`shot-${shot.id}-frame`}
              readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-frame`)}
              lockField={lockField}
              unlockField={unlockField}
            />

            {specialEquipmentField}
          </Box>

          <Box sx={singleRowNotesSx}>
            <Box sx={{ minWidth: 0 }}>
              <Stack spacing={0.75}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={groupLabelSx}>Camera Unit (Select 1)</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <ProFlag
                      type="pro"
                      scriptLevel={script ? script.features : 'Basic'}
                      scriptId={script.id}
                      handleUpgrade={() =>
                        setScript((prev) => ({
                          ...prev,
                          features: 'Pro',
                        }))
                      }
                    />
                  </Box>
                </Stack>

                <ToggleButtonGroup
                  exclusive
                  value={shot.selected_camera || ''}
                  onChange={(_e, value) => {
                    handleShotFieldChange(shot.id, 'selected_camera', value || null);
                  }}
                  onFocus={() => lockField?.(`shot-${shot.id}-selected_camera`)}
                  onBlur={() => unlockField?.(`shot-${shot.id}-selected_camera`)}
                  disabled={proDisabled}
                  sx={pillGroupSx}
                >
                  {['A', 'B', 'C', 'D'].map((val) => (
                    <ToggleButton key={val} value={val} sx={cameraBtnSx}>
                      {val}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Stack spacing={0.75}>
                <Typography sx={groupLabelSx}>Flags (Select Any)</Typography>

                <ToggleButtonGroup
                  value={[...(shot.FX ? ['FX'] : []), ...(shot.VFX ? ['VFX'] : [])]}
                  sx={{
                    ...pillGroupSx,
                    '& .MuiToggleButtonGroup-grouped': {
                      ...pillGroupSx['& .MuiToggleButtonGroup-grouped'],
                      marginLeft: '8px',
                    },
                    '& .MuiToggleButtonGroup-grouped:first-of-type': {
                      marginLeft: 0,
                    },
                  }}
                >
                  <ToggleButton
                    value="FX"
                    selected={!!shot.FX}
                    disabled={isReadOnly}
                    onClick={() => handleShotFieldChange(shot.id, 'FX', !shot.FX)}
                    sx={flagBtnSx}
                  >
                    FX
                  </ToggleButton>

                  <ToggleButton
                    value="VFX"
                    selected={!!shot.VFX}
                    disabled={isReadOnly}
                    onClick={() => handleShotFieldChange(shot.id, 'VFX', !shot.VFX)}
                    sx={flagBtnSx}
                  >
                    VFX
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                gridColumn: { xs: 'auto', sm: '1 / -1', md: 'span 2' },
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={1}
                label="Notes"
                id={`shot-${shot.id}-notes`}
                value={tempNotes[shot.id] !== undefined ? tempNotes[shot.id] : shot.notes || ''}
                onChange={(e) =>
                  setTempNotes((prev) => ({
                    ...prev,
                    [shot.id]: e.target.value,
                  }))
                }
                onBlur={() => handleUpdateNotes(scene.id, shot.id)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: isReadOnly || lockedFields.has(`shot-${shot.id}-notes`),
                }}
                disabled={isReadOnly || lockedFields.has(`shot-${shot.id}-notes`)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
                }}
              />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box className="fullWidthContainer">
            <Box className="OnePortionFlex Padded MinSmall">
              <TextField
                fullWidth
                multiline
                minRows={1}
                label="Notes"
                id={`shot-${shot.id}-notes`}
                value={tempNotes[shot.id] !== undefined ? tempNotes[shot.id] : shot.notes || ''}
                onChange={(e) =>
                  setTempNotes((prev) => ({
                    ...prev,
                    [shot.id]: e.target.value,
                  }))
                }
                onBlur={() => handleUpdateNotes(scene.id, shot.id)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: isReadOnly || lockedFields.has(`shot-${shot.id}-notes`),
                }}
                disabled={isReadOnly || lockedFields.has(`shot-${shot.id}-notes`)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
                }}
              />
            </Box>
          </Box>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 1, flexWrap: 'wrap' }}
          >
            <Box className="OnePortionFlex Padded MinSmall" sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <MuiCreatableMultiSelect
                optionList={dictionaries['movement']}
                selected={shot.movement}
                baseItem={shot}
                selectedFieldName="movement"
                changeHandler={handleShotFieldChange}
                addOption={script.level === 'owner' ? addNewUserDictionary : null}
                label="Movement"
                id={`shot-${shot.id}-movement`}
                readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-movement`)}
                lockField={lockField}
                unlockField={unlockField}
              />
            </Box>

            <Box
              className="OnePortionFlex Padded MinThird MinSmall"
              sx={{ flex: '1 1 320px', minWidth: 0 }}
            >
              <MuiCreatableMultiSelect
                optionList={dictionaries['camera']}
                selected={shot.camera}
                baseItem={shot}
                selectedFieldName="camera"
                changeHandler={handleShotFieldChange}
                addOption={script.level === 'owner' ? addNewUserDictionary : null}
                label="Camera"
                id={`shot-${shot.id}-camera`}
                readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-camera`)}
                lockField={lockField}
                unlockField={unlockField}
              />
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 1, flexWrap: 'wrap' }}
          >
            <Box className="OnePortionFlex Padded MinSmall" sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <MuiCreatableMultiSelect
                optionList={dictionaries['focus']}
                selected={shot.focus}
                baseItem={shot}
                selectedFieldName="focus"
                changeHandler={handleShotFieldChange}
                addOption={script.level === 'owner' ? addNewUserDictionary : null}
                label="Focus"
                id={`shot-${shot.id}-focus`}
                readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-focus`)}
                lockField={lockField}
                unlockField={unlockField}
              />
            </Box>

            <Box className="OnePortionFlex Padded MinSmall" sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <MuiCreatableMultiSelect
                optionList={dictionaries['frame']}
                selected={shot.frame}
                baseItem={shot}
                selectedFieldName="frame"
                changeHandler={handleShotFieldChange}
                addOption={script.level === 'owner' ? addNewUserDictionary : null}
                label="Frame"
                id={`shot-${shot.id}-frame`}
                readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-frame`)}
                lockField={lockField}
                unlockField={unlockField}
              />
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 1, flexWrap: 'wrap' }}
          >
            <Box className="OnePortionFlex Padded MinSmall" sx={{ flex: '1 1 320px', minWidth: 0 }}>
              <MuiCreatableMultiSelect
                optionList={dictionaries['focal']}
                selected={shot.focal}
                baseItem={shot}
                selectedFieldName="focal"
                changeHandler={handleShotFieldChange}
                addOption={script.level === 'owner' ? addNewUserDictionary : null}
                label="Focal"
                id={`shot-${shot.id}-focal`}
                readOnly={isReadOnly || lockedFields.has(`shot-${shot.id}-focal`)}
                lockField={lockField}
                unlockField={unlockField}
              />
            </Box>
            <Box className="OnePortionFlex Padded MinSmall" sx={{ flex: '1 1 320px', minWidth: 0 }}>
              {specialEquipmentField}
            </Box>
          </Stack>
        </>
      )}

      {!isSingleColumn && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mt: 1, p: '5px', alignItems: { md: 'flex-end' }, backgroundColor: 'transparent' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={0.75}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={groupLabelSx}>Camera Unit (Select 1)</Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ProFlag
                    type="pro"
                    scriptLevel={script ? script.features : 'Basic'}
                    scriptId={script.id}
                    handleUpgrade={() =>
                      setScript((prev) => ({
                        ...prev,
                        features: 'Pro',
                      }))
                    }
                  />
                </Box>
              </Stack>

              <ToggleButtonGroup
                exclusive
                value={shot.selected_camera || ''}
                onChange={(_e, value) => {
                  handleShotFieldChange(shot.id, 'selected_camera', value || null);
                }}
                onFocus={() => lockField?.(`shot-${shot.id}-selected_camera`)}
                onBlur={() => unlockField?.(`shot-${shot.id}-selected_camera`)}
                disabled={proDisabled}
                sx={pillGroupSx}
              >
                {['A', 'B', 'C', 'D'].map((val) => (
                  <ToggleButton key={val} value={val} sx={cameraBtnSx}>
                    {val}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              width: '1px',
              alignSelf: 'stretch',
              backgroundColor: 'divider',
              mx: 2,
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            <Stack spacing={0.75}>
              <Typography sx={groupLabelSx}>Flags (Select Any)</Typography>

              <ToggleButtonGroup
                value={[...(shot.FX ? ['FX'] : []), ...(shot.VFX ? ['VFX'] : [])]}
                sx={{
                  ...pillGroupSx,
                  '& .MuiToggleButtonGroup-grouped': {
                    ...pillGroupSx['& .MuiToggleButtonGroup-grouped'],
                    marginLeft: '8px',
                  },
                  '& .MuiToggleButtonGroup-grouped:first-of-type': {
                    marginLeft: 0,
                  },
                }}
              >
                <ToggleButton
                  value="FX"
                  selected={!!shot.FX}
                  disabled={isReadOnly}
                  onClick={() => handleShotFieldChange(shot.id, 'FX', !shot.FX)}
                  sx={flagBtnSx}
                >
                  FX
                </ToggleButton>

                <ToggleButton
                  value="VFX"
                  selected={!!shot.VFX}
                  disabled={isReadOnly}
                  onClick={() => handleShotFieldChange(shot.id, 'VFX', !shot.VFX)}
                  sx={flagBtnSx}
                >
                  VFX
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

export default ExpandedForm;
